import { factory } from '@repo-level/core-front';

export interface JackpotWin {
  jackpotWinId: number;
  reason: string;
  id: number;
  strategy: string;
  createdAt: string;
  activatable: boolean;
  activatableUntil: string;
  amountCents: number;
  amountWager_cents: number;
  amountWagerRequirementCents: number;
  cancelable: boolean;
  title: string;
  validUntil: string;
  stage: string;
  currency: string;
  groupKey: string;
  jackpot: {
    gameIdentifier: string;
    levelIndex: number;
    levelName: string;
    name: string;
    jackpotExternalCode: string;
    jackpotLevelExternalCode: string;
    winMessage: string;
  };
}

export interface Jackpot {
  allowedCurrencies: string[];
  currency: string;
  externalId: string;
  games: string[];
  id: number;
  identifier: string;
  name: string;
  state: string;
  levels: JackpotLevel[];
}

export interface JackpotLevel {
  amountCents: number;
  availableAmountCents: number;
  currentAwardValue: number | null;
  id: number;
  identifier: string;
  index: number;
  name: string;
  periods: JackpotPeriod[];
  state: string;
  totalAmountCents: number;
  workingDays: string[];
}

export interface JackpotPeriod {
  startDate: string;
  endDate: string;
}

export enum JackpotStage {
  Created = 'created',
  Active = 'active',
  Disabled = 'disabled',
  Stopped = 'stopped',
  Finished = 'finished',
}

export interface JackpotWins {
  jackpotName: string;
  jackpotLevelName: string;
  jackpotExternalCode: string;
  jackpotLevelExternalCode: string;
  userName: string;
  createdAt: string;
  approvedAt: string;
  amountCents: number;
  jackpotOriginalCurrency: string;
  jackpotOriginalAmountCents: number;
  gameIdentifier: string;
  gameProvider: string;
}

export const jackpots = factory(
  (r) => () =>
    r<Jackpot[]>('get', 'jackpots', undefined, {
      headers: {
        Accept: 'application/json',
      },
    })
);

export const jackpotWinsList = factory(
  (r) => () => r<JackpotWins[]>('get', 'jackpot_wins?per_page=100')
);

export enum LootboxStage {
  Issued = 'issued',
  Expired = 'expired',
  Lost = 'lost',
  Canceled = 'canceled',
  Activated = 'activated',
}

export enum BonusAttributeField {
  Wager = 'wager',
  BonusAmount = 'bonus_amount',
  FreespinsMaxWin = 'freespins_max_win',
  MaxWin = 'max_win',
  FreespinsCount = 'freespins_count',
}

interface BonusAttribute {
  field: BonusAttributeField;
  value: number | { currency: string; amountCents: number }[];
}

export interface LootboxBonus {
  title: string;
  type: 'freespins' | 'bonus';
  attributes?: BonusAttribute[];
  resultBonus?: BonusAttribute[];
}

export interface LootboxItem {
  bonuses: LootboxBonus[];
  createdAt: string;
  id: number;
  prize: boolean;
  weight: number;
}

export interface Lootbox {
  createdAt: string;
  display: number;
  groupKey: string;
  id: number;
  items: LootboxItem[];
  stage: LootboxStage;
  strategy: string;
  title: string;
  validUntil: string;
}

export const playerLootboxes = factory(
  (r) => () => r<Lootbox[]>('get', 'player/lootboxes')
);

export const playerLootboxActivation = factory(
  (r) => (id: number) =>
    r<LootboxItem>('post', `player/lootboxes/${id}/activation`)
);

export interface PlayerTransaction {
  id: number;
  amountCents: number;
  currency: string;
  action: string;
  paymentSystem: string;
  recallable: boolean;
  createdAt: string;
  finishedAt: string;
  systemName?: string;
  state?: string;
  brand?: string;
  success: boolean;
}

export interface ReferralProgramRealTime {
  text: string;
}

export enum PromocodeStatus {
  SuccessfullyActivated = 'successfully_activated',
  FailedToActivate = 'failed_to_activate',
}

export interface PromocodeActivation {
  status: PromocodeStatus;
}

export const promocodeActivation = factory(
  (r) =>
    ({ promocode }: { promocode: string }) =>
      r<PromocodeActivation>('post', 'bonuses/coupon', {
        coupon_code: promocode,
      })
);

export interface CompointsRatesMoney {
  group: string;
  rates: { points: number; amountCents: number; currency: string }[];
  title: string;
  wager: number;
}

export const compPointsRatesMoney = factory(
  (r) => () => r<CompointsRatesMoney[]>('get', 'comp_points/rates/money')
);

export interface CompointsRatesMoney {
  group: string;
  rates: { points: number; amountCents: number; currency: string }[];
  title: string;
  wager: number;
}

export interface PlayerCompoints {
  chargeable: {
    points: number;
  };
  persistent: {
    points: number;
  };
}

export const playerCompoints = factory(
  (r) => () => r<PlayerCompoints>('get', 'player/comp_points')
);

interface ExchangeCompointsMoneyApi {
  group: string;
  points: number;
  currency: string;
}

export const exchangeCompointsMoney = factory(
  (r) => (data: ExchangeCompointsMoneyApi) =>
    r('post', 'comp_points/exchange/money', { exchange: data })
);

export enum BonusStage {
  Issued = 'issued',
  Expired = 'expired',
  HandleBets = 'handle_bets',
  Canceled = 'canceled',
  WagerDone = 'wager_done',
  Activated = 'activated',
  Finished = 'finished',
  Lost = 'lost',
}

enum BonusStrategy {
  Deposit = 'deposit',
  Exchange = 'exchange',
  FreespinsResult = 'freespins_result',
  GroupsUpdated = 'groups_updated',
  InputCoupon = 'input_coupon',
  JackpotAward = 'jackpot_award',
  Manual = 'manual',
  Prize = 'prize',
  PrizeAward = 'prize_award',
  Registration = 'registration',
  Scheduler = 'scheduler',
}

export interface BonusStageI {
  stage: BonusStage;
}

export interface PlayerBonus extends BonusStageI {
  id: number;
  title: string;
  amountCents: number;
  amountWagerCents: number;
  amountWagerRequirementCents: number;
  maxWin: number;
  currency: string;
  strategy: BonusStrategy;
  createdAt: string;
  validUntil: string | null;
  activatableUntil: string | null;
  cancelable: boolean;
  activatable: boolean;
  gamesInfo?: {
    identifier: string;
    title: string;
    frontendUrl: string;
  }[];
  games?: string[];
  jackpot?: {
    gameIdentifier: string;
    name: string;
    levelName: string;
    levelIndex: number;
    jackpotExternalCode: string;
    jackpotLevelExternalCode: string;
    winMessage: string;
  };
  groupKey?: string;
}

export const playerBonuses = factory(
  (r) => () => r<PlayerBonus[]>('get', 'player/bonuses')
);

export interface PlayerFreespin extends BonusStageI {
  id: number;
  title: string;
  freespinsTotal: number;
  freespinsPerformed: number | null;
  games: string[];
  activationPath: string;
  provider: string;
  activatableUntil: string | null;
  validUntil: string | null;
  activatable: boolean;
  activationCondition: null;
  cancelable: boolean;
  createdAt: string;
  betLevel: number;
  currency: string;
  ceCurrency: string | null;
  groupKey?: string;
  freespinsMaxWin?: number;
  gamesInfo?: {
    identifier: string;
    title: string;
    frontendUrl: string;
  }[];
}

export const playerFreespins = factory(
  (r) => () => r<PlayerFreespin[]>('get', 'player/freespins')
);

export const infoProject = factory(
  (r) => () => r<{ availableReferralSystem: boolean }>('get', 'info/project')
);

export const playerBonusesActivation = factory(
  (r) => (id: number) => r('post', `player/bonuses/${id}/activation`)
);

export const playerFreespinsActivation = factory(
  (r) => (id: number) => r('post', `player/freespins/${id}/activation`)
);

export const playerBonusesCancellation = factory(
  (r) => (id: number) => r('delete', `player/bonuses/${id}`)
);

export const playerFreespinsCancellation = factory(
  (r) => (id: number) => r('delete', `player/freespins/${id}`)
);
